import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import * as NGL from "ngl";
import { getChainColor } from "../utils/chainColors";

// Register a custom NGL color scheme once, keyed to the exact same palette
// (getChainColor) the 2D interaction graph and Legend already use. Replaces
// NGL's built-in "chainid" scheme, which colors by a chain's index within
// the whole downloaded PDB entry (e.g. "24 chain(s)" for a full asymmetric
// unit) rather than by the specific chain letters this app actually shows
// -- previously a chain could render green in the graph and crimson in the
// structure purely because of where it happened to fall in the full
// entry's chain list, breaking the entire premise of a linked view.
const hexToInt = hex => parseInt(String(hex).replace("#", ""), 16);
const CHAIN_COLOR_SCHEME_ID = NGL.ColormakerRegistry.addScheme(function chainColorScheme() {
  this.atomColor = atom => hexToInt(getChainColor(atom.chainname));
}, "predppi-chain-colors");

const buildChainSelection = chains =>
  (Array.isArray(chains) ? chains : [])
    .filter(Boolean)
    .map(chain => `:${chain}`)
    .join(" or ");

const buildResidueSelection = residues => {
  const residueList = Array.from(new Set((Array.isArray(residues) ? residues : []).filter(Boolean)));
  if (residueList.length === 0) return "";

  return residueList.map(residue => `resi ${residue}`).join(" or ");
};

const applyRepresentations = ({
  component,
  stage,
  highlightChains,
  visibleChains,
  baseHighlightResidues,
  hoveredResidues,
  selectedResidues,
  visibleRepresentations,
  shouldAutoView = false
}) => {
  if (!component || !stage) return;

  // Chain Visibility must be the final word on what's actually rendered --
  // every other selection (the "highlight" overlay, residue highlights) is
  // scoped down to the currently visible chains, not just the base cartoon.
  // Otherwise an overlay for a chain the user just hid can silently repaint
  // it back in on top of the correctly-updated base layer.
  const visibleChainList = Array.isArray(visibleChains) ? visibleChains.filter(Boolean) : [];
  const isChainVisible = chain => visibleChainList.length === 0 || visibleChainList.includes(chain);

  const effectiveHighlightChains = (Array.isArray(highlightChains) ? highlightChains : []).filter(isChainVisible);
  const chainSelection = buildChainSelection(effectiveHighlightChains);
  const visibleChainSelection = buildChainSelection(visibleChains);
  const proteinSelection = visibleChainSelection || "protein";

  // Residue selections (`resi N`) carry no chain qualifier, so without this
  // they'd still render on a hidden chain that happens to share a residue
  // number with a visible one. Scoping them keeps every representation
  // consistent with Chain Visibility, not just the cartoon.
  const scopeToVisibleChains = selection =>
    selection && visibleChainSelection ? `(${selection}) and (${visibleChainSelection})` : selection;

  const baseResidueSelection = scopeToVisibleChains(buildResidueSelection(baseHighlightResidues));
  const hoveredResidueSelection = scopeToVisibleChains(buildResidueSelection(hoveredResidues));
  const selectedResidueSelection = scopeToVisibleChains(buildResidueSelection(selectedResidues));

  // The Surface mesh is the entire visible protein and, even at low opacity,
  // its overdraw washes out the highlighted-residue representations sitting
  // underneath it (the actual reason this match exists to look at). Carve a
  // literal gap in the surface at every highlighted residue so those atoms
  // are never behind translucent geometry, instead of relying on z-order or
  // opacity tricks to fight it.
  const highlightedResidueSelection = [baseResidueSelection, hoveredResidueSelection, selectedResidueSelection]
    .filter(Boolean)
    .map(selection => `(${selection})`)
    .join(" or ");
  const surfaceSelection = highlightedResidueSelection
    ? `(${proteinSelection}) and not (${highlightedResidueSelection})`
    : proteinSelection;

  const showCartoon = visibleRepresentations?.cartoon !== false;
  const showSurface = Boolean(visibleRepresentations?.surface);
  const showBallStick = Boolean(visibleRepresentations?.ballStick);
  const showChains = visibleRepresentations?.chains !== false;

  component.removeAllRepresentations();

  if (showCartoon) {
    component.addRepresentation("cartoon", {
      sele: proteinSelection,
      color: showChains ? CHAIN_COLOR_SCHEME_ID : "lightgrey"
    });
  }

  if (showSurface) {
    component.addRepresentation("surface", {
      sele: surfaceSelection,
      opacity: 0.18,
      color: showChains ? CHAIN_COLOR_SCHEME_ID : "lightgrey"
    });
  }

  if (chainSelection) {
    component.addRepresentation("cartoon", {
      sele: chainSelection,
      color: CHAIN_COLOR_SCHEME_ID
    });
  }

  if (baseResidueSelection) {
    component.addRepresentation(showBallStick ? "ball+stick" : "spacefill", {
      sele: baseResidueSelection,
      color: "#ef4444",
      scale: showBallStick ? 1.2 : 0.55
    });
  }

  if (selectedResidueSelection) {
    component.addRepresentation("ball+stick", {
      sele: selectedResidueSelection,
      color: "#dc2626",
      scale: 1.8
    });
    component.addRepresentation("spacefill", {
      sele: selectedResidueSelection,
      color: "#f87171",
      opacity: 0.3,
      scale: 0.9
    });
  }

  if (hoveredResidueSelection) {
    component.addRepresentation("ball+stick", {
      sele: hoveredResidueSelection,
      color: "#f59e0b",
      scale: 2.3
    });
    component.addRepresentation("spacefill", {
      sele: hoveredResidueSelection,
      color: "#facc15",
      opacity: 0.45,
      scale: 1.15
    });
  }

  if (shouldAutoView) {
    component.autoView();
  }

  stage.viewer.requestRender();
};

export default function ProteinStructureViewer({
  pdbId,
  highlightChains = [],
  visibleChains = [],
  baseHighlightResidues = [],
  hoveredResidues = [],
  selectedResidues = [],
  visibleRepresentations,
  minHeight = 420
}) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const componentRef = useRef(null);
  const fittedPdbIdRef = useRef("");
  const [viewerState, setViewerState] = useState({
    loading: true,
    error: "",
    structureStats: null
  });

  useEffect(() => {
    if (!pdbId || !containerRef.current) {
      componentRef.current = null;
      fittedPdbIdRef.current = "";
      setViewerState({
        loading: false,
        error: "No structure selected.",
        structureStats: null
      });
      return undefined;
    }

    let disposed = false;
    const container = containerRef.current;

    if (stageRef.current) {
      try {
        stageRef.current.dispose();
      } catch (error) {
        console.warn("ProteinStructureViewer pre-reset warning:", error);
      }
      stageRef.current = null;
    }

    componentRef.current = null;
    fittedPdbIdRef.current = "";
    container.innerHTML = "";

    setViewerState({
      loading: true,
      error: "",
      structureStats: null
    });

    const stage = new NGL.Stage(container, {
      backgroundColor: "white"
    });
    stageRef.current = stage;
    stage.setParameters({
      clipNear: 0,
      clipFar: 100,
      cameraType: "perspective",
      quality: "medium",
      sampleLevel: 0,
      impostor: true
    });

    stage.loadFile(`https://files.rcsb.org/download/${String(pdbId).toUpperCase()}.pdb`, {
      defaultRepresentation: false,
      firstModelOnly: true
    }).then(component => {
      if (disposed) return;
      const structureStats = {
        atomCount: component?.structure?.atomCount || 0,
        residueCount: component?.structure?.residueStore?.count || 0,
        chainCount: component?.structure?.chainStore?.count || 0
      };

      const isLargeStructure = structureStats.atomCount > 18000 || structureStats.residueCount > 2200;
      if (isLargeStructure) {
        stage.setParameters({
          quality: "low",
          sampleLevel: -1
        });
      }

      componentRef.current = component;
      applyRepresentations({
        component,
        stage,
        highlightChains,
        visibleChains,
        baseHighlightResidues,
        hoveredResidues,
        selectedResidues,
        visibleRepresentations,
        shouldAutoView: true
      });
      fittedPdbIdRef.current = String(pdbId).toUpperCase();
      stage.handleResize();

      setViewerState({
        loading: false,
        error: "",
        structureStats
      });
    }).catch(() => {
      if (disposed) return;

      setViewerState({
        loading: false,
        error: `Unable to load structure ${pdbId}.`,
        structureStats: null
      });
    });

    const handleResize = () => {
      if (stageRef.current) {
        stageRef.current.handleResize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      disposed = true;
      window.removeEventListener("resize", handleResize);

      if (stageRef.current === stage) {
        stageRef.current = null;
      }
      componentRef.current = null;
      fittedPdbIdRef.current = "";

      try {
        stage.dispose();
      } catch (error) {
        console.warn("ProteinStructureViewer cleanup warning:", error);
      }

      if (container) {
        container.innerHTML = "";
      }
    };
  }, [pdbId]);

  useEffect(() => {
    const component = componentRef.current;
    const stage = stageRef.current;
    if (!component || !stage || !pdbId) {
      return;
    }

    applyRepresentations({
      component,
      stage,
      highlightChains,
      visibleChains,
      baseHighlightResidues,
      hoveredResidues,
      selectedResidues,
      visibleRepresentations,
      shouldAutoView: false
    });

    if (fittedPdbIdRef.current !== String(pdbId).toUpperCase()) {
      component.autoView();
      fittedPdbIdRef.current = String(pdbId).toUpperCase();
    }
  }, [baseHighlightResidues, highlightChains, hoveredResidues, pdbId, selectedResidues, visibleChains, visibleRepresentations]);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid #d5dfeb",
        backgroundColor: "#f8fbff"
      }}
    >
      {viewerState.loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 1
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={28} />
            <Typography sx={{ mt: 1.5, color: "#526173", fontSize: 13 }}>
              Loading structure {String(pdbId).toUpperCase()}...
            </Typography>
          </Box>
        </Box>
      )}

      {viewerState.error && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 3,
            backgroundColor: "rgba(255, 255, 255, 0.88)",
            zIndex: 1
          }}
        >
          <Box>
            <Typography color="error" sx={{ fontWeight: 600 }}>
              {viewerState.error}
            </Typography>
            <Typography sx={{ mt: 1, color: "#607086", fontSize: 13 }}>
              Try switching the structure focus or opening the classic graph page if this structure remains unavailable.
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          minHeight
        }}
      />

      {!viewerState.loading && !viewerState.error && viewerState.structureStats && (
        <Box
          sx={{
            position: "absolute",
            left: 12,
            bottom: 12,
            px: 1.25,
            py: 0.75,
            borderRadius: 2,
            backgroundColor: "rgba(255,255,255,0.9)",
            border: "1px solid rgba(148, 163, 184, 0.45)"
          }}
        >
          <Typography sx={{ color: "#526173", fontSize: 12 }}>
            {viewerState.structureStats.chainCount} chain(s) | {viewerState.structureStats.residueCount} residues | {viewerState.structureStats.atomCount} atoms
          </Typography>
        </Box>
      )}
    </Box>
  );
}
